import { NgModule, Type, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, RouterModule, RouterStateSnapshot, Routes, UrlTree } from '@angular/router';

import { Observable } from 'rxjs';

import { Store } from '@ngrx/store';

// Components are loaded dynamically using loadComponent() in route configurations
import { TokenActions } from '../store/token.actions';
import { WarnOnDeactivateComponent } from '../warn-on-deactivate-component';

import { JwtHelperService } from '@auth0/angular-jwt';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
  const store: Store = inject(Store);

  // FIXME: reimplement using NgRx
  const token = localStorage.getItem('authentication');

  if (new JwtHelperService().isTokenExpired(token)) {
    console.debug('Dispatching logout from canActivate');
    store.dispatch(TokenActions.logout());
    return false;
  }

  return true;
}

function canDeactivateEditPage(
  component: WarnOnDeactivateComponent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentRoute: ActivatedRouteSnapshot,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentState: RouterStateSnapshot,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nextState?: RouterStateSnapshot,
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
  if (component.changed) {
    return component.showSaveWarning$();
  }

  return true;
}

interface GenericRouteOptions {
  path: string;
  listComponent?: Type<unknown>;
  editComponent?: Type<unknown>;
  loadListComponent?: () => Promise<Type<unknown>>;
  loadEditComponent?: () => Promise<Type<unknown>>;
  recordKeyName?: string;
  children?: Route[];
}

function createGenericRoutes(options: GenericRouteOptions): Route {
  if (!options.listComponent && !options.loadListComponent) {
    throw new Error('Either listComponent or loadListComponent must be provided');
  }

  if (!options.editComponent && !options.loadEditComponent) {
    throw new Error('Either editComponent or loadEditComponent must be provided');
  }

  const baseChildren: Route[] = [
    {
      path: 'list',
      pathMatch: 'full',
      ...(options.loadListComponent ? { loadComponent: options.loadListComponent } : { component: options.listComponent }),
    },
    {
      path: 'create',
      pathMatch: 'full',
      ...(options.loadEditComponent ? { loadComponent: options.loadEditComponent } : { component: options.editComponent }),
      canDeactivate: [canDeactivateEditPage],
    },
    {
      path: `edit/:${options.recordKeyName || 'id'}`,
      pathMatch: 'full',
      ...(options.loadEditComponent ? { loadComponent: options.loadEditComponent } : { component: options.editComponent }),
      data: { recordKeyName: options.recordKeyName || 'id' },
      canDeactivate: [canDeactivateEditPage],
    },
  ];

  if (options.children) {
    baseChildren.push(...options.children);
  }

  return {
    path: options.path,
    children: baseChildren,
  };
}

// Routes using createGenericRoutes
function createAdministratorRoutes(): Route {
  return createGenericRoutes({
    path: 'administrators',
    loadListComponent: () =>
      import('../administrator-list-page/administrator-list-page.component').then((m) => m.AdministratorListPageComponent),
    loadEditComponent: () =>
      import('../administrator-edit-page/administrator-edit-page.component').then((m) => m.AdministratorEditPageComponent),
    recordKeyName: 'administratorId',
  });
}

function createAgendaCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'agenda_categories',
    loadListComponent: () =>
      import('../agenda-category-list-page/agenda-category-list-page.component').then((m) => m.AgendaCategoryListPageComponent),
    loadEditComponent: () =>
      import('../agenda-category-edit-page/agenda-category-edit-page.component').then((m) => m.AgendaCategoryEditPageComponent),
    recordKeyName: 'agendaCategoryId',
  });
}

function createAgendaSubcategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'agenda_subcategories',
    loadListComponent: () =>
      import('../agenda-subcategory-list-page/agenda-subcategory-list-page.component').then((m) => m.AgendaSubcategoryListPageComponent),
    loadEditComponent: () =>
      import('../agenda-subcategory-edit-page/agenda-subcategory-edit-page.component').then((m) => m.AgendaSubcategoryEditPageComponent),
    recordKeyName: 'agendaSubcategoryId',
  });
}

function createCarReservationRoutes(): Route {
  return createGenericRoutes({
    path: 'car_reservations',
    loadListComponent: () =>
      import('../car-reservation-list-page/car-reservation-list-page.component').then((m) => m.CarReservationListPageComponent),
    loadEditComponent: () =>
      import('../car-reservation-edit-page/car-reservation-edit-page.component').then((m) => m.CarReservationEditPageComponent),
    recordKeyName: 'carReservationId',
  });
}

function createChatGroupRoutes(): Route {
  return createGenericRoutes({
    path: 'chat_groups',
    loadListComponent: () => import('../chat-group-list-page/chat-group-list-page.component').then((m) => m.ChatGroupListPageComponent),
    loadEditComponent: () => import('../chat-group-edit-page/chat-group-edit-page.component').then((m) => m.ChatGroupEditPageComponent),
    recordKeyName: 'chatGroupId',
  });
}

function createCompanyFileRoutes(): Route {
  return createGenericRoutes({
    path: 'company_files',
    loadListComponent: () =>
      import('../company-file-list-page/company-file-list-page.component').then((m) => m.CompanyFileListPageComponent),
    loadEditComponent: () =>
      import('../company-file-edit-page/company-file-edit-page.component').then((m) => m.CompanyFileEditPageComponent),
    recordKeyName: 'companyFileId',
  });
}

function createDocumentRoutes(): Route {
  return createGenericRoutes({
    path: 'documents',
    loadListComponent: () => import('../document-list-page/document-list-page.component').then((m) => m.DocumentListPageComponent),
    loadEditComponent: () => import('../document-edit-page/document-edit-page.component').then((m) => m.DocumentEditPageComponent),
    recordKeyName: 'documentId',
  });
}

function createEventCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'event_categories',
    loadListComponent: () =>
      import('../event-category-list-page/event-category-list-page.component').then((m) => m.EventCategoryListPageComponent),
    loadEditComponent: () =>
      import('../event-category-edit-page/event-category-edit-page.component').then((m) => m.EventCategoryEditPageComponent),
    recordKeyName: 'eventCategoryId',
  });
}

function createParticipantCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'participant_categories',
    loadListComponent: () =>
      import('../participant-category-list-page/participant-category-list-page.component').then(
        (m) => m.ParticipantCategoryListPageComponent,
      ),
    loadEditComponent: () =>
      import('../participant-category-edit-page/participant-category-edit-page.component').then(
        (m) => m.ParticipantCategoryEditPageComponent,
      ),
    recordKeyName: 'participantCategoryId',
  });
}

function createUserCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'user_categories',
    loadListComponent: () =>
      import('../user-category-list-page/user-category-list-page.component').then((m) => m.UserCategoryListPageComponent),
    loadEditComponent: () =>
      import('../user-category-edit-page/user-category-edit-page.component').then((m) => m.UserCategoryEditPageComponent),
    recordKeyName: 'userCategoryId',
  });
}

function createEventSponsorTierRoutes(): Route {
  return createGenericRoutes({
    path: 'event_sponsor_tiers',
    loadListComponent: () =>
      import('../event-sponsor-tier-list-page/event-sponsor-tier-list-page.component').then((m) => m.EventSponsorTierListPageComponent),
    loadEditComponent: () =>
      import('../event-sponsor-tier-edit-page/event-sponsor-tier-edit-page.component').then((m) => m.EventSponsorTierEditPageComponent),
    recordKeyName: 'eventSponsorTierId',
  });
}

function createEventSubcategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'event_subcategories',
    loadListComponent: () =>
      import('../event-subcategory-list-page/event-subcategory-list-page.component').then((m) => m.EventSubcategoryListPageComponent),
    loadEditComponent: () =>
      import('../event-subcategory-edit-page/event-subcategory-edit-page.component').then((m) => m.EventSubcategoryEditPageComponent),
    recordKeyName: 'eventSubcategoryId',
  });
}

function createEventUserRoutes(): Route {
  return createGenericRoutes({
    path: 'event_users',
    loadListComponent: () => import('../event-user-list-page/event-user-list-page.component').then((m) => m.EventUserListPageComponent),
    loadEditComponent: () => import('../event-user-edit-page/event-user-edit-page.component').then((m) => m.EventUserEditPageComponent),
    recordKeyName: 'eventUserId',
  });
}

function createUserSubscriptionRoutes(): Route {
  return createGenericRoutes({
    path: 'user_subscriptions',
    loadListComponent: () =>
      import('../user-subscription-list-page/user-subscription-list-page.component').then((m) => m.UserSubscriptionListPageComponent),
    loadEditComponent: () =>
      import('../user-subscription-edit-page/user-subscription-edit-page.component').then((m) => m.UserSubscriptionEditPageComponent),
    recordKeyName: 'userSubscriptionId',
  });
}

function createExhibitorBrandRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitor_brands',
    loadListComponent: () =>
      import('../exhibitor-brand-list-page/exhibitor-brand-list-page.component').then((m) => m.ExhibitorBrandListPageComponent),
    loadEditComponent: () =>
      import('../exhibitor-brand-edit-page/exhibitor-brand-edit-page.component').then((m) => m.ExhibitorBrandEditPageComponent),
    recordKeyName: 'exhibitorBrandId',
  });
}

function createExhibitorDocumentRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitor_documents',
    loadListComponent: () =>
      import('../exhibitor-document-list-page/exhibitor-document-list-page.component').then((m) => m.ExhibitorDocumentListPageComponent),
    loadEditComponent: () =>
      import('../exhibitor-document-edit-page/exhibitor-document-edit-page.component').then((m) => m.ExhibitorDocumentEditPageComponent),
    recordKeyName: 'exhibitorDocumentId',
  });
}

function createExhibitorPressReleaseRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitor_press_releases',
    loadListComponent: () =>
      import('../exhibitor-press-release-list-page/exhibitor-press-release-list-page.component').then(
        (m) => m.ExhibitorPressReleaseListPageComponent,
      ),
    loadEditComponent: () =>
      import('../exhibitor-press-release-edit-page/exhibitor-press-release-edit-page.component').then(
        (m) => m.ExhibitorPressReleaseEditPageComponent,
      ),
    recordKeyName: 'exhibitorPressReleaseId',
  });
}

function createExhibitorProductRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitor_products',
    loadListComponent: () =>
      import('../exhibitor-product-list-page/exhibitor-product-list-page.component').then((m) => m.ExhibitorProductListPageComponent),
    loadEditComponent: () =>
      import('../exhibitor-product-edit-page/exhibitor-product-edit-page.component').then((m) => m.ExhibitorProductEditPageComponent),
    recordKeyName: 'exhibitorProductId',
  });
}

function createExhibitorVideosRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitor_videos',
    loadListComponent: () =>
      import('../exhibitor-video-list-page/exhibitor-video-list-page.component').then((m) => m.ExhibitorVideoListPageComponent),
    loadEditComponent: () =>
      import('../exhibitor-video-edit-page/exhibitor-video-edit-page.component').then((m) => m.ExhibitorVideoEditPageComponent),
    recordKeyName: 'exhibitorVideoId',
  });
}

function createFeedbackVoteRoutes(): Route {
  return createGenericRoutes({
    path: 'feedback_votes',
    loadListComponent: () =>
      import('../feedback-vote-list-page/feedback-vote-list-page.component').then((m) => m.FeedbackVoteListPageComponent),
    loadEditComponent: () =>
      import('../feedback-vote-edit-page/feedback-vote-edit-page.component').then((m) => m.FeedbackVoteEditPageComponent),
    recordKeyName: 'feedbackVoteId',
  });
}

function createFieldOfApplicationRoutes(): Route {
  return createGenericRoutes({
    path: 'fields_of_application',
    loadListComponent: () =>
      import('../field-of-application-list-page/field-of-application-list-page.component').then(
        (m) => m.FieldOfApplicationListPageComponent,
      ),
    loadEditComponent: () =>
      import('../field-of-application-edit-page/field-of-application-edit-page.component').then(
        (m) => m.FieldOfApplicationEditPageComponent,
      ),
    recordKeyName: 'fieldOfApplicationId',
  });
}

function createFirmRoutes(): Route {
  return createGenericRoutes({
    path: 'firms',
    loadListComponent: () => import('../firm-list-page/firm-list-page.component').then((m) => m.FirmListPageComponent),
    loadEditComponent: () => import('../firm-edit-page/firm-edit-page.component').then((m) => m.FirmEditPageComponent),
    recordKeyName: 'firmId',
  });
}

function createFlightReservationRoutes(): Route {
  return createGenericRoutes({
    path: 'flight_reservations',
    loadListComponent: () =>
      import('../flight-reservation-list-page/flight-reservation-list-page.component').then((m) => m.FlightReservationListPageComponent),
    loadEditComponent: () =>
      import('../flight-reservation-edit-page/flight-reservation-edit-page.component').then((m) => m.FlightReservationEditPageComponent),
    recordKeyName: 'flightReservationId',
  });
}

function createGroupUserRoutes(): Route {
  return createGenericRoutes({
    path: 'group_users',
    loadListComponent: () => import('../group-user-list-page/group-user-list-page.component').then((m) => m.GroupUserListPageComponent),
    loadEditComponent: () => import('../group-user-edit-page/group-user-edit-page.component').then((m) => m.GroupUserEditPageComponent),
    recordKeyName: 'groupUserId',
  });
}

function createGroupRoutes(): Route {
  return createGenericRoutes({
    path: 'groups',
    loadListComponent: () => import('../group-list-page/group-list-page.component').then((m) => m.GroupListPageComponent),
    loadEditComponent: () => import('../group-edit-page/group-edit-page.component').then((m) => m.GroupEditPageComponent),
    recordKeyName: 'groupId',
    children: [
      {
        path: ':groupId',
        children: [createGroupUserRoutes()],
      },
    ],
  });
}

function createHotelReservationRoutes(): Route {
  return createGenericRoutes({
    path: 'hotel_reservations',
    loadListComponent: () =>
      import('../hotel-reservation-list-page/hotel-reservation-list-page.component').then((m) => m.HotelReservationListPageComponent),
    loadEditComponent: () =>
      import('../hotel-reservation-edit-page/hotel-reservation-edit-page.component').then((m) => m.HotelReservationEditPageComponent),
    recordKeyName: 'hotelReservationId',
  });
}

function createEventHotelRoomRoutes(): Route {
  return createGenericRoutes({
    path: 'event_hotel_rooms',
    loadListComponent: () =>
      import('../event-hotel-room-list-page/event-hotel-room-list-page.component').then((m) => m.EventHotelRoomListPageComponent),
    loadEditComponent: () =>
      import('../event-hotel-room-edit-page/event-hotel-room-edit-page.component').then((m) => m.EventHotelRoomEditPageComponent),
    recordKeyName: 'eventHotelRoomId',
  });
}

function createHotelRoutes(): Route {
  return createGenericRoutes({
    path: 'hotels',
    loadListComponent: () => import('../hotel-list-page/hotel-list-page.component').then((m) => m.HotelListPageComponent),
    loadEditComponent: () => import('../hotel-edit-page/hotel-edit-page.component').then((m) => m.HotelEditPageComponent),
    recordKeyName: 'hotelId',
  });
}

function createMailTemplateRoutes(): Route {
  return createGenericRoutes({
    path: 'mail_templates',
    loadListComponent: () =>
      import('../mail-template-list-page/mail-template-list-page.component').then((m) => m.MailTemplateListPageComponent),
    loadEditComponent: () =>
      import('../mail-template-edit-page/mail-template-edit-page.component').then((m) => m.MailTemplateEditPageComponent),
    recordKeyName: 'mailTemplateId',
  });
}

function createMerchandiseCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'merchandise_categories',
    loadListComponent: () =>
      import('../merchandise-category-list-page/merchandise-category-list-page.component').then(
        (m) => m.MerchandiseCategoryListPageComponent,
      ),
    loadEditComponent: () =>
      import('../merchandise-category-edit-page/merchandise-category-edit-page.component').then(
        (m) => m.MerchandiseCategoryEditPageComponent,
      ),
    recordKeyName: 'merchandiseCategoryId',
  });
}

function createNewsCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'news_categories',
    loadListComponent: () =>
      import('../news-category-list-page/news-category-list-page.component').then((m) => m.NewsCategoryListPageComponent),
    loadEditComponent: () =>
      import('../news-category-edit-page/news-category-edit-page.component').then((m) => m.NewsCategoryEditPageComponent),
    recordKeyName: 'newsCategoryId',
  });
}

function createNewsItemRoutes(): Route {
  return createGenericRoutes({
    path: 'news_items',
    loadListComponent: () => import('../news-item-list-page/news-item-list-page.component').then((m) => m.NewsItemListPageComponent),
    loadEditComponent: () => import('../news-item-edit-page/news-item-edit-page.component').then((m) => m.NewsItemEditPageComponent),
    recordKeyName: 'newsItemId',
  });
}

function createPDFTemplateRoutes(): Route {
  return createGenericRoutes({
    path: 'pdf_templates',
    loadListComponent: () =>
      import('../pdf-template-list-page/pdf-template-list-page.component').then((m) => m.PDFTemplateListPageComponent),
    loadEditComponent: () =>
      import('../pdf-template-edit-page/pdf-template-edit-page.component').then((m) => m.PDFTemplateEditPageComponent),
    recordKeyName: 'pdfTemplateId',
  });
}

function createPollRoutes(): Route {
  return createGenericRoutes({
    path: 'polls',
    loadListComponent: () => import('../poll-list-page/poll-list-page.component').then((m) => m.PollListPageComponent),
    loadEditComponent: () => import('../poll-edit-page/poll-edit-page.component').then((m) => m.PollEditPageComponent),
    recordKeyName: 'pollId',
  });
}

function createRestaurantRoutes(): Route {
  return createGenericRoutes({
    path: 'restaurants',
    loadListComponent: () => import('../restaurant-list-page/restaurant-list-page.component').then((m) => m.RestaurantListPageComponent),
    loadEditComponent: () => import('../restaurant-edit-page/restaurant-edit-page.component').then((m) => m.RestaurantEditPageComponent),
    recordKeyName: 'restaurantId',
  });
}

function createShuttleScheduleRoutes(): Route {
  return createGenericRoutes({
    path: 'shuttle_schedules',
    loadListComponent: () =>
      import('../shuttle-schedule-list-page/shuttle-schedule-list-page.component').then((m) => m.ShuttleScheduleListPageComponent),
    loadEditComponent: () =>
      import('../shuttle-schedule-edit-page/shuttle-schedule-edit-page.component').then((m) => m.ShuttleScheduleEditPageComponent),
    recordKeyName: 'shuttleScheduleId',
  });
}

function createSpeakerRoutes(): Route {
  return createGenericRoutes({
    path: 'speakers',
    loadListComponent: () => import('../speaker-list-page/speaker-list-page.component').then((m) => m.SpeakerListPageComponent),
    loadEditComponent: () => import('../speaker-edit-page/speaker-edit-page.component').then((m) => m.SpeakerEditPageComponent),
    recordKeyName: 'speakerId',
  });
}

function createSponsorRoutes(): Route {
  return createGenericRoutes({
    path: 'sponsors',
    loadListComponent: () => import('../sponsor-list-page/sponsor-list-page.component').then((m) => m.SponsorListPageComponent),
    loadEditComponent: () => import('../sponsor-edit-page/sponsor-edit-page.component').then((m) => m.SponsorEditPageComponent),
    recordKeyName: 'sponsorId',
  });
}

function createStreamRoutes(): Route {
  return createGenericRoutes({
    path: 'streams',
    loadListComponent: () => import('../stream-list-page/stream-list-page.component').then((m) => m.StreamListPageComponent),
    loadEditComponent: () => import('../stream-edit-page/stream-edit-page.component').then((m) => m.StreamEditPageComponent),
    recordKeyName: 'streamId',
  });
}

function createTradeShowSectorRoutes(): Route {
  return createGenericRoutes({
    path: 'trade_show_sectors',
    loadListComponent: () =>
      import('../trade-show-sector-list-page/trade-show-sector-list-page.component').then((m) => m.TradeShowSectorListPageComponent),
    loadEditComponent: () =>
      import('../trade-show-sector-edit-page/trade-show-sector-edit-page.component').then((m) => m.TradeShowSectorEditPageComponent),
    recordKeyName: 'trade_show_sector_id',
  });
}

function createTrainReservationRoutes(): Route {
  return createGenericRoutes({
    path: 'train_reservations',
    loadListComponent: () =>
      import('../train-reservation-list-page/train-reservation-list-page.component').then((m) => m.TrainReservationListPageComponent),
    loadEditComponent: () =>
      import('../train-reservation-edit-page/train-reservation-edit-page.component').then((m) => m.TrainReservationEditPageComponent),
    recordKeyName: 'trainReservationId',
  });
}

function createUserDocumentRoutes(): Route {
  return createGenericRoutes({
    path: 'user_documents',
    loadListComponent: () =>
      import('../user-document-list-page/user-document-list-page.component').then((m) => m.UserDocumentListPageComponent),
    loadEditComponent: () =>
      import('../user-document-edit-page/user-document-edit-page.component').then((m) => m.UserDocumentEditPageComponent),
    recordKeyName: 'userDocumentId',
  });
}

function createUserGeneratedContentRoutes(): Route {
  return createGenericRoutes({
    path: 'user_generated_contents',
    loadListComponent: () =>
      import('../user-generated-content-list-page/user-generated-content-list-page.component').then(
        (m) => m.UserGeneratedContentListPageComponent,
      ),
    loadEditComponent: () =>
      import('../user-generated-content-edit-page/user-generated-content-edit-page.component').then(
        (m) => m.UserGeneratedContentEditPageComponent,
      ),
    recordKeyName: 'userGeneratedContentId',
  });
}

function createUserRoutes(): Route {
  return createGenericRoutes({
    path: 'users',
    loadListComponent: () => import('../user-list-page/user-list-page.component').then((m) => m.UserListPageComponent),
    loadEditComponent: () => import('../user-edit-page/user-edit-page.component').then((m) => m.UserEditPageComponent),
    recordKeyName: 'userId',
    children: [
      {
        path: ':userId',
        children: [createUserSubscriptionRoutes()],
      },
    ],
  });
}

function createVenueCategoryRoutes(): Route {
  return createGenericRoutes({
    path: 'venue_categories',
    loadListComponent: () =>
      import('../venue-category-list-page/venue-category-list-page.component').then((m) => m.VenueCategoryListPageComponent),
    loadEditComponent: () =>
      import('../venue-category-edit-page/venue-category-edit-page.component').then((m) => m.VenueCategoryEditPageComponent),
    recordKeyName: 'venueCategoryId',
  });
}

function createVenueRoutes(): Route {
  return createGenericRoutes({
    path: 'venues',
    loadListComponent: () => import('../venue-list-page/venue-list-page.component').then((m) => m.VenueListPageComponent),
    loadEditComponent: () => import('../venue-edit-page/venue-edit-page.component').then((m) => m.VenueEditPageComponent),
    recordKeyName: 'venueId',
  });
}

function createDeviceTokenRoutes(): Route {
  return createGenericRoutes({
    path: 'device_tokens',
    loadListComponent: () =>
      import('../device-token-list-page/device-token-list-page.component').then((m) => m.DeviceTokenListPageComponent),
    loadEditComponent: () =>
      import('../device-token-edit-page/device-token-edit-page.component').then((m) => m.DeviceTokenEditPageComponent),
    recordKeyName: 'deviceTokenId',
  });
}

function createGigyaKeyDataRoutes(): Route {
  return createGenericRoutes({
    path: 'gigya_key_data',
    loadListComponent: () =>
      import('../gigya-key-data-list-page/gigya-key-data-list-page.component').then((m) => m.GigyaKeyDataListPageComponent),
    loadEditComponent: () =>
      import('../gigya-key-data-edit-page/gigya-key-data-edit-page.component').then((m) => m.GigyaKeyDataEditPageComponent),
    recordKeyName: 'gigyaKeyDataId',
  });
}

function createRegistrationFormRoutes(): Route {
  return createGenericRoutes({
    path: 'registration_forms',
    loadListComponent: () =>
      import('../registration-form-list-page/registration-form-list-page.component').then((m) => m.RegistrationFormListPageComponent),
    loadEditComponent: () =>
      import('../registration-form-edit-page/registration-form-edit-page.component').then((m) => m.RegistrationFormEditPageComponent),
    recordKeyName: 'registrationFormId',
  });
}

// Complex routes with children
function createAgendaRouteConfig(): Route {
  return createGenericRoutes({
    path: 'agendas',
    loadListComponent: () => import('../agenda-list-page/agenda-list-page.component').then((m) => m.AgendaListPageComponent),
    loadEditComponent: () => import('../agenda-edit-page/agenda-edit-page.component').then((m) => m.AgendaEditPageComponent),
    recordKeyName: 'agendaId',
    children: [
      {
        path: 'edit/:agendaId',
        children: [
          createGenericRoutes({
            path: 'agenda_users',
            loadListComponent: () =>
              import('../agenda-user-list-page/agenda-user-list-page.component').then((m) => m.AgendaUserListPageComponent),
            loadEditComponent: () =>
              import('../agenda-user-edit-page/agenda-user-edit-page.component').then((m) => m.AgendaUserEditPageComponent),
            recordKeyName: 'agendaUserId',
          }),
        ],
      },
    ],
  });
}

function createMeetingRoutes(): Route {
  return createGenericRoutes({
    path: 'meetings',
    loadListComponent: () => import('../meeting-list-page/meeting-list-page.component').then((m) => m.MeetingListPageComponent),
    loadEditComponent: () => import('../meeting-edit-page/meeting-edit-page.component').then((m) => m.MeetingEditPageComponent),
    recordKeyName: 'meetingId',
  });
}

function createLiveChatRoutes(): Route {
  return createGenericRoutes({
    path: 'livechats',
    loadListComponent: () => import('../livechat-list-page/livechat-list-page.component').then((m) => m.LivechatListPageComponent),
    loadEditComponent: () => import('../livechat-edit-page/livechat-edit-page.component').then((m) => m.LivechatEditPageComponent),
    recordKeyName: 'livechatId',
  });
}

function createWallRoutes(): Route {
  return createGenericRoutes({
    path: 'walls',
    loadListComponent: () => import('../wall-list-page/wall-list-page.component').then((m) => m.WallListPageComponent),
    loadEditComponent: () => import('../wall-edit-page/wall-edit-page.component').then((m) => m.WallEditPageComponent),
    recordKeyName: 'wallId',
  });
}

function createEventRoutes(): Route {
  return createGenericRoutes({
    path: 'events',
    loadListComponent: () => import('../event-list-page/event-list-page.component').then((m) => m.EventListPageComponent),
    loadEditComponent: () => import('../event-edit-page/event-edit-page.component').then((m) => m.EventEditPageComponent),
    recordKeyName: 'eventId',
    children: [
      {
        path: 'setup/:eventId',
        loadComponent: () => import('../event-setup-page/event-setup-page.component').then((m) => m.EventSetupPageComponent),
        canDeactivate: [canDeactivateEditPage],
        children: [
          {
            path: 'settings',
            loadComponent: () => import('../event-edit-page/event-edit-page.component').then((m) => m.EventEditPageComponent),
            data: { recordKeyName: 'eventId' },
            canDeactivate: [canDeactivateEditPage],
          },
          {
            path: 'menu_builder',
            loadComponent: () => import('../menu-builder-page/menu-builder-page.component').then((m) => m.MenuBuilderPageComponent),
            canDeactivate: [canDeactivateEditPage],
          },
          createDocumentRoutes(),
          createAgendaRouteConfig(),
          createStreamRoutes(),
          createEventUserRoutes(),
          createExhibitorRoutes(),
          createTradeShowSectorRoutes(),
          createMerchandiseCategoryRoutes(),
          createFieldOfApplicationRoutes(),
          createUserDocumentRoutes(),
          createUserGeneratedContentBinRoutes(),
          createRestaurantRoutes(),
          createShuttleScheduleRoutes(),
          createFlightReservationRoutes(),
          createCarReservationRoutes(),
          createHotelReservationRoutes(),
          createEventHotelRoomRoutes(),
          createTrainReservationRoutes(),
          createFeedbackRoutes(),
          createPollRoutes(),
          createEventSponsorTierRoutes(),
          createMeetingRoutes(),
          createLiveChatRoutes(),
          createWallRoutes(),
        ],
      },
    ],
  });
}

function createExhibitorRoutes(): Route {
  return createGenericRoutes({
    path: 'exhibitors',
    loadListComponent: () => import('../exhibitor-list-page/exhibitor-list-page.component').then((m) => m.ExhibitorListPageComponent),
    loadEditComponent: () => import('../exhibitor-edit-page/exhibitor-edit-page.component').then((m) => m.ExhibitorEditPageComponent),
    recordKeyName: 'exhibitorId',
    children: [
      {
        path: ':exhibitorId',
        children: [
          createExhibitorBrandRoutes(),
          createExhibitorProductRoutes(),
          createExhibitorPressReleaseRoutes(),
          createExhibitorVideosRoutes(),
          createExhibitorDocumentRoutes(),
        ],
      },
    ],
  });
}

function createFeedbackQuestionRoutes(): Route {
  return createGenericRoutes({
    path: 'feedback_questions',
    loadListComponent: () =>
      import('../feedback-question-list-page/feedback-question-list-page.component').then((m) => m.FeedbackQuestionListPageComponent),
    loadEditComponent: () =>
      import('../feedback-question-edit-page/feedback-question-edit-page.component').then((m) => m.FeedbackQuestionEditPageComponent),
    recordKeyName: 'feedbackQuestionId',
    children: [
      {
        path: ':feedbackQuestionId',
        children: [createFeedbackVoteRoutes()],
      },
    ],
  });
}

function createFeedbackRoutes(): Route {
  return createGenericRoutes({
    path: 'feedbacks',
    loadListComponent: () => import('../feedback-list-page/feedback-list-page.component').then((m) => m.FeedbackListPageComponent),
    loadEditComponent: () => import('../feedback-edit-page/feedback-edit-page.component').then((m) => m.FeedbackEditPageComponent),
    recordKeyName: 'feedbackId',
    children: [
      {
        path: ':feedbackId',
        children: [createFeedbackQuestionRoutes()],
      },
    ],
  });
}

function createMailRoute(): Route {
  return {
    path: 'mail',
    loadComponent: () => import('../send-mail-page/send-mail-page.component').then((m) => m.SendMailPageComponent),
    data: { title: 'Mail' },
  };
}

function createPushRoute(): Route {
  return {
    path: 'push',
    loadComponent: () => import('../send-push-page/send-push-page.component').then((m) => m.SendPushPageComponent),
    data: { title: 'Push Notifications' },
  };
}

function createSentMailRoutes(): Route {
  return {
    path: 'sent_mails',
    children: [
      {
        path: 'list',
        loadComponent: () => import('../sent-mail-list-page/sent-mail-list-page.component').then((m) => m.SentMailListPageComponent),
      },
      // NOTE: not yet implemented
      // {
      //   path: 'view/:sentMailId',
      //   loadComponent: () => import('../sent-mail-edit-page/sent-mail-edit-page.component').then((m) => m.SentMailEditPageComponent),
      //   data: { recordKeyName: 'sentMailId' },
      // },
    ],
  };
}

function createSentPushRoutes(): Route {
  return {
    path: 'sent_pushes',
    children: [
      {
        path: 'list',
        loadComponent: () => import('../sent-push-list-page/sent-push-list-page.component').then((m) => m.SentPushListPageComponent),
      },
    ],
  };
}

function createTenantRoutes(): Route {
  return createGenericRoutes({
    path: 'tenants',
    loadListComponent: () => import('../company-list-page/company-list-page.component').then((m) => m.CompanyListPageComponent),
    loadEditComponent: () => import('../company-edit-page/company-edit-page.component').then((m) => m.CompanyEditPageComponent),
    recordKeyName: 'companyId',
    children: [
      {
        path: 'theme_settings/:companyId',
        loadComponent: () =>
          import('../theme-setting-edit-page/theme-setting-edit-page.component').then((m) => m.ThemeSettingEditPageComponent),
        data: { recordKeyName: 'companyId' },
        canDeactivate: [canDeactivateEditPage],
      },
    ],
  });
}

function createUserGeneratedContentBinRoutes(): Route {
  return createGenericRoutes({
    path: 'user_generated_content_bins',
    loadListComponent: () =>
      import('../user-generated-content-bin-list-page/user-generated-content-bin-list-page.component').then(
        (m) => m.UserGeneratedContentBinListPageComponent,
      ),
    loadEditComponent: () =>
      import('../user-generated-content-bin-edit-page/user-generated-content-bin-edit-page.component').then(
        (m) => m.UserGeneratedContentBinEditPageComponent,
      ),
    recordKeyName: 'userGeneratedContentBinId',
    children: [
      {
        path: ':userGeneratedContentBinId',
        children: [createUserGeneratedContentRoutes()],
      },
    ],
  });
}

function createSubscriptionRoutes(): Route {
  return createGenericRoutes({
    path: 'subscriptions',
    loadListComponent: () =>
      import('../subscription-list-page/subscription-list-page.component').then((m) => m.SubscriptionListPageComponent),
    loadEditComponent: () =>
      import('../subscription-edit-page/subscription-edit-page.component').then((m) => m.SubscriptionEditPageComponent),
    recordKeyName: 'subscriptionId',
  });
}

function createBackendHelpRoutes(): Route {
  return createGenericRoutes({
    path: 'backend_help',
    loadListComponent: () =>
      import('../backend-help-list-page/backend-help-list-page.component').then((m) => m.BackendHelpListPageComponent),
    loadEditComponent: () =>
      import('../backend-help-edit-page/backend-help-edit-page.component').then((m) => m.BackendHelpEditPageComponent),
    recordKeyName: 'backendHelpId',
  });
}

function createMediaGalleryItemRoutes(): Route {
  return createGenericRoutes({
    path: 'media_gallery_items',
    loadListComponent: () =>
      import('../media-gallery-item-list-page/media-gallery-item-list-page.component').then((m) => m.MediaGalleryItemListPageComponent),
    loadEditComponent: () =>
      import('../media-gallery-item-edit-page/media-gallery-item-edit-page.component').then((m) => m.MediaGalleryItemEditPageComponent),
    recordKeyName: 'mediaGalleryItemId',
  });
}

function createMediaGalleryRoutes(): Route {
  return createGenericRoutes({
    path: 'media_gallery',
    loadListComponent: () =>
      import('../media-gallery-list-page/media-gallery-list-page.component').then((m) => m.MediaGalleryListPageComponent),
    loadEditComponent: () =>
      import('../media-gallery-edit-page/media-gallery-edit-page.component').then((m) => m.MediaGalleryEditPageComponent),
    recordKeyName: 'mediaGalleryId',
    children: [
      {
        path: ':mediaGalleryId',
        children: [createMediaGalleryItemRoutes()],
      },
    ],
  });
}

// Routes configuration
const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/auth/login',
  },
  {
    path: 'teleprompter/:event_id',
    loadComponent: () => import('../livechat-teleprompter/livechat-teleprompter.component').then((m) => m.LivechatTeleprompterComponent),
  },
  {
    path: 'main',
    loadComponent: () => import('../main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    canActivate: [canActivate],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
      {
        path: 'icons',
        loadComponent: () => import('../icon-page/icon-page.component').then((m) => m.IconPageComponent),
        data: { title: 'Icons' },
      },
      {
        path: 'colors',
        loadComponent: () => import('../color-showcase-page/color-showcase-page.component').then((m) => m.ColorShowcasePageComponent),
        data: { title: 'Theme Colors' },
      },
      {
        path: 'home',
        loadComponent: () =>
          import('../company-selection-page/company-selection-page.component').then((m) => m.CompanySelectionPageComponent),
        children: [],
      },
      {
        path: 'dashboard',
        loadComponent: () => import('../dashboard-page/dashboard-page.component').then((m) => m.DashboardPageComponent),
        data: { title: 'Dashboard' },
      },
      {
        path: 'administration',
        children: [createTenantRoutes(), createAdministratorRoutes()],
      },
      {
        path: 'crm',
        children: [createUserRoutes(), createUserCategoryRoutes(), createGroupRoutes(), createFirmRoutes()],
      },
      {
        path: 'cms',
        children: [
          createEventCategoryRoutes(),
          createEventSubcategoryRoutes(),
          createParticipantCategoryRoutes(),
          createAgendaCategoryRoutes(),
          createAgendaSubcategoryRoutes(),
          createNewsCategoryRoutes(),
          createVenueCategoryRoutes(),
          createVenueRoutes(),
          createDeviceTokenRoutes(),
          createGigyaKeyDataRoutes(),
          createRegistrationFormRoutes(),
          createEventRoutes(),
          createSpeakerRoutes(),
          createSponsorRoutes(),
          createHotelRoutes(),
          createCompanyFileRoutes(),
          createNewsItemRoutes(),
          createSubscriptionRoutes(),
          createMediaGalleryRoutes(),
        ],
      },
      {
        path: 'interactivity',
        children: [
          createChatGroupRoutes(),
          {
            path: 'playlist',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Playlist' },
          },
          {
            path: 'games',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Games' },
          },
          {
            path: 'popups',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Popups' },
          },
        ],
      },
      {
        path: 'reports',
        children: [
          {
            path: 'user-report',
            loadComponent: () => import('../user-report-page/user-report-page.component').then((m) => m.UserReportPageComponent),
            data: { title: 'User Report' },
          },
          {
            path: 'report_builder',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Report Builder' },
          },
        ],
      },
      {
        path: 'communication',
        children: [
          createMailRoute(),
          createPushRoute(),
          createMailTemplateRoutes(),
          createSentMailRoutes(),
          createSentPushRoutes(),
          createPDFTemplateRoutes(),
          {
            path: 'sms',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'SMS' },
          },
          {
            path: 'whatsapp',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'WhatsApp' },
          },
          {
            path: 'helpdesk',
            loadComponent: () => import('../help-desk-page/help-desk-page.component').then((m) => m.HelpDeskPageComponent),
          },
        ],
      },
      {
        path: 'settings',
        children: [
          {
            path: 'company_settings',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Company Settings' },
          },
          {
            path: 'translations',
            loadComponent: () => import('../placeholder-page/placeholder-page.component').then((m) => m.PlaceholderPageComponent),
            data: { title: 'Translations' },
          },
          createBackendHelpRoutes(),
        ],
      },
    ],
  },
  {
    path: 'auth',
    loadComponent: () => import('../auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
      {
        path: 'login',
        loadComponent: () => import('../login-page/login-page.component').then((m) => m.LoginPageComponent),
        children: [],
      },
      {
        path: 'password-reset-request/:email',
        loadComponent: () =>
          import('../password-reset-request-page/password-reset-request-page.component').then((m) => m.PasswordResetRequestPageComponent),
        children: [],
      },
      {
        path: 'password-reset',
        loadComponent: () => import('../password-reset-page/password-reset-page.component').then((m) => m.PasswordResetPageComponent),
        children: [],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableTracing: false, paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
